import { useCallback, useEffect, useState } from 'react';

import { CheckboxAlign, CheckboxVerticalAlign, Loader } from '@calm-web/design-system';

import AppleAuthButton from '@/components/authForm/AppleAuthButton';
import AuthTerms from '@/components/authForm/AuthTerms';
import EmailAuthButton from '@/components/authForm/EmailAuthButton';
import FacebookAuthButton from '@/components/authForm/FacebookAuthButton';
import GoogleAuthButton from '@/components/authForm/GoogleAuthButton';
import ModeToggle from '@/components/authForm/ModeToggle';
import { useAuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useAuth } from '@/context/auth/AuthProvider';
import { FormStateContextProvider } from '@/context/FormStateContextProvider';
import { useAnalytics } from '@/hooks/analytics';
import { useRecaptchaVisibleState } from '@/hooks/store';
import { User } from '@/store/user/types';
import { getUserData } from '@/utils/apiRequest/getUserData';
import { postDevice } from '@/utils/endpoints';

import { SocialSignupWrapper, StyledMarketingOptIn, Wrapper } from './styles';
import { AuthFormProps } from './types';

const AuthForm = ({
	onAuthSuccess,
	className,
	facebookButtonText,
	googleButtonText,
	loginTermsText,
	signupTermsText,
	source,
	disableFormToggle = false,
	lightText = false,
	customEventProps,
	loginHint,
}: AuthFormProps) => {
	const { isLoading } = useAuth();
	const recaptchaVisible = useRecaptchaVisibleState();

	const [marketingOptIn, setMarketingOptIn] = useState(true);
	const { authPrompt } = useAuthPrompt();
	const { logEvent } = useAnalytics();

	const onChangeMarketingOptIn = useCallback((value: boolean) => setMarketingOptIn(value), []);

	async function _onAuthSuccess(user: User) {
		if (onAuthSuccess) {
			await onAuthSuccess(user);
		}

		// adding a POST /device call here so that we save user level info like timezone that we get from the browser
		// instead of info coming from server where we make additional POST /device calls
		await postDevice({ userData: getUserData() });
	}

	useEffect(() => {
		const sourceProp: Record<string, string> = source ? { source } : {};
		const customProps = customEventProps ? customEventProps : {};

		logEvent({
			eventName: 'Login Form : Landed',
			eventProps: {
				...sourceProp,
				...customProps,
				mode: authPrompt,
			},
		});
	}, [logEvent, authPrompt, source, customEventProps]);

	if (isLoading) {
		return <Loader color="gray8" />;
	}

	return (
		<FormStateContextProvider>
			<Wrapper className={className} lightText={lightText} id="auth-form">
				<>
					<SocialSignupWrapper>
						<li>
							<EmailAuthButton
								marketingOptIn={marketingOptIn}
								onAuthSuccess={_onAuthSuccess}
								loginHint={loginHint}
							/>
						</li>
						<li>
							<FacebookAuthButton
								onAuthSuccess={_onAuthSuccess}
								buttonText={facebookButtonText}
								marketingOptIn={marketingOptIn}
							/>
						</li>
						<li>
							<AppleAuthButton onAuthSuccess={_onAuthSuccess} marketingOptIn={marketingOptIn} />
						</li>
						<li>
							<GoogleAuthButton
								onAuthSuccess={_onAuthSuccess}
								buttonText={googleButtonText}
								marketingOptIn={marketingOptIn}
							/>
						</li>
					</SocialSignupWrapper>
					{!recaptchaVisible && (
						<AuthTerms
							loginTermsText={loginTermsText}
							signupTermsText={signupTermsText}
							lightText={lightText}
						/>
					)}
					{authPrompt === 'signup' && (
						<StyledMarketingOptIn
							onChange={onChangeMarketingOptIn}
							alignment={CheckboxAlign.Left}
							verticalAlignment={CheckboxVerticalAlign.Top}
						/>
					)}
					{!disableFormToggle && <ModeToggle />}
				</>
			</Wrapper>
		</FormStateContextProvider>
	);
};

export default AuthForm;
